import React from "react";

/* ================================================================
   Daily Life Scenes — 8 animated templates
   Kitchen, classroom, office, playground, park, shopping, dining, bedroom
   Flat 2D illustration style with subtle animations
   ================================================================ */

// ── 1. Kitchen Scene ─────────────────────────────────────────────────
export const KitchenScene: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const items = ["Stove", "Fridge", "Sink", "Counter"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Floor & wall */}
      <rect x="0" y="0" width="800" height="300" fill="#fef3c7" rx="12" />
      <rect x="0" y="300" width="800" height="200" fill="#d4a574" rx="0" />
      <line x1="0" y1="300" x2="800" y2="300" stroke="#92400e" strokeWidth="2" />
      {/* Stove */}
      <g opacity={activeIndex === 0 ? 1 : 0.6}>
        <rect x="60" y="200" width="140" height="100" rx="5" fill="#475569" stroke="#334155" strokeWidth="2" />
        <circle cx="100" cy="230" r="18" fill="#1e293b" /><circle cx="160" cy="230" r="18" fill="#1e293b" />
        {/* Flame */}
        <path d="M95,225 Q100,210 105,225" fill="#ef4444" opacity={activeIndex === 0 ? 1 : 0.3}>
          <animate attributeName="d" values="M95,225 Q100,210 105,225;M93,225 Q100,208 107,225;M95,225 Q100,210 105,225" dur="0.5s" repeatCount="indefinite" />
        </path>
        <text x="130" y="330" textAnchor="middle" fill="#92400e" fontSize="11">Stove</text>
      </g>
      {/* Fridge */}
      <g opacity={activeIndex === 1 ? 1 : 0.6}>
        <rect x="240" y="100" width="100" height="200" rx="5" fill="#e0e7ff" stroke="#94a3b8" strokeWidth="2" />
        <line x1="240" y1="200" x2="340" y2="200" stroke="#94a3b8" strokeWidth="1.5" />
        <rect x="330" y="140" width="5" height="30" rx="2" fill="#94a3b8" />
        <rect x="330" y="220" width="5" height="20" rx="2" fill="#94a3b8" />
        <text x="290" y="330" textAnchor="middle" fill="#92400e" fontSize="11">Fridge</text>
      </g>
      {/* Sink */}
      <g opacity={activeIndex === 2 ? 1 : 0.6}>
        <rect x="400" y="220" width="120" height="80" rx="8" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="2" />
        <ellipse cx="460" cy="240" rx="35" ry="15" fill="#94a3b8" />
        {/* Faucet */}
        <path d="M460,200 L460,210 Q470,210 475,220" stroke="#6b7280" strokeWidth="4" fill="none" />
        {/* Water drops */}
        <circle cx="465" cy="228" r="2" fill="#60a5fa">
          <animate attributeName="cy" values="225;260;225" dur="1s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite" />
        </circle>
        <text x="460" y="330" textAnchor="middle" fill="#92400e" fontSize="11">Sink</text>
      </g>
      {/* Counter */}
      <g opacity={activeIndex === 3 ? 1 : 0.6}>
        <rect x="570" y="230" width="180" height="70" rx="4" fill="#a16207" stroke="#854d0e" strokeWidth="2" />
        {/* Items on counter */}
        <rect x="590" y="210" width="20" height="20" rx="3" fill="#ef4444" />
        <circle cx="640" cy="215" r="12" fill="#22c55e" />
        <rect x="680" y="205" width="15" height="25" rx="2" fill="#f59e0b" />
        <text x="660" y="330" textAnchor="middle" fill="#92400e" fontSize="11">Counter</text>
      </g>
      {/* Active indicator */}
      <text x="400" y="460" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">{items[activeIndex]}</text>
    </svg>
  );
};

// ── 2. Classroom Scene ───────────────────────────────────────────────
export const ClassroomScene: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Blackboard */}
      <rect x="150" y="30" width="500" height="150" rx="6" fill="#065f46" stroke="#854d0e" strokeWidth="5" />
      <text x="400" y="100" textAnchor="middle" fill="#d1fae5" fontSize="20" fontFamily="serif">Welcome to Class!</text>
      <text x="400" y="140" textAnchor="middle" fill="#a7f3d0" fontSize="12">Today's Topic: Learning</text>
      {/* Teacher desk */}
      <rect x="300" y="200" width="200" height="40" rx="4" fill="#92400e" />
      <rect x="370" y="240" width="60" height="30" rx="3" fill="#78350f" />
      {/* Student desks - 3 rows of 3 */}
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => {
          const idx = row * 3 + col;
          const dx = 150 + col * 200;
          const dy = 300 + row * 55;
          const isActive = idx === activeIndex;
          return (
            <g key={`${row}-${col}`}>
              {/* Desk */}
              <rect x={dx} y={dy} width="100" height="25" rx="3" fill={isActive ? "#1e1b4b" : "#334155"} stroke={isActive ? "#6366f1" : "#475569"} strokeWidth="1.5" />
              {/* Student head */}
              <circle cx={dx + 50} cy={dy - 12} r="10" fill={["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6", "#f97316"][idx]} opacity={isActive ? 1 : 0.6}>
                {isActive && <animate attributeName="r" values="10;12;10" dur="1.5s" repeatCount="indefinite" />}
              </circle>
            </g>
          );
        })
      )}
      <text x="400" y="480" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Classroom</text>
    </svg>
  );
};

// ── 3. Office Desk ───────────────────────────────────────────────────
export const OfficeDesk: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const cursorBlink = Math.sin(phase * 3) > 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Desk surface */}
      <rect x="100" y="250" width="600" height="30" rx="3" fill="#a16207" />
      <rect x="120" y="280" width="30" height="140" rx="3" fill="#854d0e" />
      <rect x="650" y="280" width="30" height="140" rx="3" fill="#854d0e" />
      {/* Monitor */}
      <rect x="280" y="100" width="240" height="145" rx="6" fill="#1e293b" stroke="#475569" strokeWidth="3" />
      <rect x="290" y="110" width="220" height="120" rx="3" fill="#0f172a" />
      <rect x="380" y="245" width="40" height="10" fill="#475569" />
      <rect x="360" y="253" width="80" height="5" rx="2" fill="#475569" />
      {/* Screen content */}
      <rect x="300" y="120" width="200" height="10" rx="2" fill="#334155" />
      <rect x="300" y="140" width="160" height="8" rx="2" fill="#334155" />
      <rect x="300" y="155" width="180" height="8" rx="2" fill="#334155" />
      <rect x="300" y="170" width="120" height="8" rx="2" fill="#334155" />
      {/* Cursor */}
      {cursorBlink && <rect x="420" y="170" width="2" height="10" fill="#6366f1" />}
      {/* Keyboard */}
      <rect x="310" y="260" width="180" height="15" rx="3" fill="#334155" />
      {Array.from({ length: 10 }).map((_, i) => (
        <rect key={i} x={315 + i * 17} y="262" width="12" height="4" rx="1" fill="#475569" />
      ))}
      {/* Mouse */}
      <ellipse cx="540" cy="268" rx="12" ry="15" fill="#475569" stroke="#334155" strokeWidth="1" />
      {/* Coffee cup */}
      <rect x="160" y="230" width="25" height="20" rx="3" fill="#78350f" />
      <path d="M185,235 Q195,240 185,250" stroke="#78350f" strokeWidth="2" fill="none" />
      {/* Steam */}
      <path d="M170,225 Q175,215 170,205" stroke="#94a3b8" strokeWidth="1.5" fill="none" opacity="0.4">
        <animate attributeName="opacity" values="0;0.4;0" dur="2s" repeatCount="indefinite" />
        <animateTransform attributeName="transform" type="translate" values="0,0;-3,-5;0,0" dur="2s" repeatCount="indefinite" />
      </path>
      {/* Plant */}
      <rect x="620" y="220" width="20" height="30" rx="3" fill="#92400e" />
      <circle cx="630" cy="210" r="15" fill="#22c55e" />
      <circle cx="620" cy="200" r="10" fill="#16a34a" />
      <text x="400" y="460" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Work Desk</text>
    </svg>
  );
};

// ── 4. Playground ────────────────────────────────────────────────────
export const Playground: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const swingAngle = Math.sin(phase * 1.2) * 25;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Sky */}
      <rect x="0" y="0" width="800" height="350" fill="#bfdbfe" rx="12" />
      {/* Sun */}
      <circle cx="680" cy="80" r="40" fill="#fbbf24" opacity="0.9" />
      {/* Clouds */}
      <g>
        <ellipse cx="200" cy="60" rx="60" ry="25" fill="#fff" opacity="0.8">
          <animateTransform attributeName="transform" type="translate" values="0,0;20,0;0,0" dur="8s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="500" cy="90" rx="45" ry="20" fill="#fff" opacity="0.6">
          <animateTransform attributeName="transform" type="translate" values="0,0;-15,0;0,0" dur="10s" repeatCount="indefinite" />
        </ellipse>
      </g>
      {/* Ground */}
      <rect x="0" y="350" width="800" height="150" fill="#22c55e" rx="0" />
      {/* Swing set */}
      <line x1="200" y1="150" x2="200" y2="350" stroke="#78350f" strokeWidth="5" />
      <line x1="350" y1="150" x2="350" y2="350" stroke="#78350f" strokeWidth="5" />
      <line x1="190" y1="150" x2="360" y2="150" stroke="#78350f" strokeWidth="6" />
      {/* Swing chain + seat */}
      <g transform={`rotate(${swingAngle},275,150)`}>
        <line x1="275" y1="150" x2="275" y2="300" stroke="#6b7280" strokeWidth="2" />
        <rect x="260" y="295" width="30" height="8" rx="2" fill="#ef4444" />
        {/* Kid */}
        <circle cx="275" cy="280" r="10" fill="#fbbf24" />
        <rect x="270" y="290" width="10" height="5" rx="2" fill="#3b82f6" />
      </g>
      {/* Slide */}
      <line x1="500" y1="180" x2="500" y2="350" stroke="#78350f" strokeWidth="5" />
      <line x1="500" y1="180" x2="650" y2="340" stroke="#ef4444" strokeWidth="8" strokeLinecap="round" />
      <line x1="480" y1="180" x2="500" y2="180" stroke="#78350f" strokeWidth="4" />
      {/* Seesaw */}
      <polygon points="130,390 140,370 150,390" fill="#78350f" />
      <line x1="80" y1="375" x2="200" y2="385" stroke="#f59e0b" strokeWidth="6" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" values="-3,140,380;3,140,380;-3,140,380" dur="2s" repeatCount="indefinite" />
      </line>
      <text x="400" y="470" textAnchor="middle" fill="#166534" fontSize="16" fontWeight="bold">Playground</text>
    </svg>
  );
};

// ── 5. Park Bench ────────────────────────────────────────────────────
export const ParkBench: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Sky */}
      <rect x="0" y="0" width="800" height="320" fill="#dbeafe" rx="12" />
      {/* Trees */}
      <rect x="100" y="200" width="15" height="120" fill="#78350f" />
      <circle cx="107" cy="180" r="40" fill="#16a34a" />
      <circle cx="85" cy="190" r="25" fill="#22c55e" />
      <circle cx="130" cy="195" r="28" fill="#15803d" />
      <rect x="650" y="210" width="12" height="110" fill="#78350f" />
      <circle cx="656" cy="185" r="35" fill="#16a34a" />
      <circle cx="638" cy="200" r="22" fill="#22c55e" />
      {/* Ground */}
      <rect x="0" y="320" width="800" height="180" fill="#86efac" />
      <path d="M0,320 Q200,310 400,320 Q600,330 800,320" fill="#4ade80" />
      {/* Bench */}
      <rect x="300" y="300" width="200" height="12" rx="3" fill="#92400e" />
      <rect x="300" y="318" width="200" height="8" rx="2" fill="#78350f" />
      <rect x="310" y="326" width="8" height="40" rx="2" fill="#78350f" />
      <rect x="482" y="326" width="8" height="40" rx="2" fill="#78350f" />
      {/* Person sitting */}
      <circle cx="380" cy="275" r="15" fill="#fbbf24" />
      <rect x="370" y="290" width="20" height="15" rx="4" fill="#6366f1" />
      <rect x="366" y="305" width="10" height="20" rx="3" fill="#4f46e5" />
      <rect x="384" y="305" width="10" height="20" rx="3" fill="#4f46e5" />
      {/* Bird */}
      <g>
        <ellipse cx={500 + Math.sin(phase * 0.5) * 30} cy={150 + Math.cos(phase * 0.3) * 15} rx="8" ry="5" fill="#475569" />
        <path d={`M${492 + Math.sin(phase * 0.5) * 30},${147 + Math.cos(phase * 0.3) * 15} Q${488 + Math.sin(phase * 0.5) * 30},${140 + Math.cos(phase * 0.3) * 15} ${485 + Math.sin(phase * 0.5) * 30},${145 + Math.cos(phase * 0.3) * 15}`} stroke="#475569" strokeWidth="2" fill="none">
          <animate attributeName="d" values="M492,147 Q488,140 485,145;M492,147 Q488,135 485,142;M492,147 Q488,140 485,145" dur="0.8s" repeatCount="indefinite" />
        </path>
      </g>
      {/* Flowers */}
      {[200, 550, 630].map((x, i) => (
        <g key={i}>
          <line x1={x} y1="360" x2={x} y2="340" stroke="#16a34a" strokeWidth="2" />
          <circle cx={x} cy="336" r="5" fill={["#ef4444", "#fbbf24", "#ec4899"][i]} />
        </g>
      ))}
      <text x="400" y="470" textAnchor="middle" fill="#166534" fontSize="14" fontWeight="bold">Peaceful Park</text>
    </svg>
  );
};

// ── 6. Shopping Cart ─────────────────────────────────────────────────
export const ShoppingCart: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const items = ["Bread", "Milk", "Fruit", "Vegetables", "Cheese"];
  const colors = ["#f59e0b", "#e0e7ff", "#ef4444", "#22c55e", "#fbbf24"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Cart body */}
      <path d="M250,180 L280,350 L550,350 L570,180 Z" fill="none" stroke="#6b7280" strokeWidth="3" />
      <line x1="280" y1="350" x2="220" y2="160" stroke="#6b7280" strokeWidth="3" />
      <line x1="220" y1="160" x2="180" y2="160" stroke="#6b7280" strokeWidth="4" strokeLinecap="round" />
      {/* Wheels */}
      <circle cx="310" cy="370" r="15" fill="none" stroke="#475569" strokeWidth="3" />
      <circle cx="310" cy="370" r="4" fill="#475569" />
      <circle cx="520" cy="370" r="15" fill="none" stroke="#475569" strokeWidth="3" />
      <circle cx="520" cy="370" r="4" fill="#475569" />
      {/* Items in cart */}
      {items.slice(0, activeIndex + 1).map((item, i) => {
        const ix = 290 + (i % 3) * 80;
        const iy = 200 + Math.floor(i / 3) * 70;
        return (
          <g key={i}>
            <rect x={ix} y={iy} width="60" height="50" rx="8" fill={colors[i]} opacity={i === activeIndex ? 1 : 0.6}>
              {i === activeIndex && <animate attributeName="opacity" values="0.6;1;0.6" dur="1s" repeatCount="indefinite" />}
            </rect>
            <text x={ix + 30} y={iy + 30} textAnchor="middle" fill="#1e293b" fontSize="10" fontWeight="bold">{item}</text>
          </g>
        );
      })}
      {/* Item count */}
      <circle cx="580" cy="170" r="18" fill="#ef4444" />
      <text x="580" y="175" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="bold">{activeIndex + 1}</text>
      <text x="400" y="440" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Shopping — {items[activeIndex]}</text>
    </svg>
  );
};

// ── 7. Dining Table ──────────────────────────────────────────────────
export const DiningTable: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Table top - isometric */}
      <polygon points="400,180 650,250 400,320 150,250" fill="#a16207" stroke="#854d0e" strokeWidth="2" />
      {/* Table legs */}
      <line x1="200" y1="260" x2="200" y2="400" stroke="#854d0e" strokeWidth="6" />
      <line x1="600" y1="260" x2="600" y2="400" stroke="#854d0e" strokeWidth="6" />
      {/* Plates */}
      {[{ x: 300, y: 230 }, { x: 500, y: 230 }, { x: 300, y: 280 }, { x: 500, y: 280 }].map((p, i) => (
        <g key={i} opacity={i === activeIndex ? 1 : 0.5}>
          <ellipse cx={p.x} cy={p.y} rx="40" ry="20" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1.5" />
          <ellipse cx={p.x} cy={p.y} rx="30" ry="15" fill="#f1f5f9" />
          {i === activeIndex && (
            <ellipse cx={p.x} cy={p.y} rx="42" ry="22" fill="none" stroke="#6366f1" strokeWidth="1.5">
              <animate attributeName="opacity" values="0.3;0.8;0.3" dur="1.5s" repeatCount="indefinite" />
            </ellipse>
          )}
          {/* Fork & knife */}
          <line x1={p.x - 50} y1={p.y - 5} x2={p.x - 50} y2={p.y + 20} stroke="#94a3b8" strokeWidth="2" />
          <line x1={p.x + 50} y1={p.y - 5} x2={p.x + 50} y2={p.y + 20} stroke="#94a3b8" strokeWidth="2" />
        </g>
      ))}
      {/* Centerpiece */}
      <rect x="385" y="245" width="30" height="15" rx="3" fill="#f59e0b" />
      <circle cx="400" cy="238" r="8" fill="#ef4444" />
      {/* Chairs */}
      <rect x="250" y="350" width="40" height="8" rx="2" fill="#78350f" />
      <rect x="510" y="350" width="40" height="8" rx="2" fill="#78350f" />
      <text x="400" y="460" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Dinner Table</text>
    </svg>
  );
};

// ── 8. Bedroom Scene ─────────────────────────────────────────────────
export const BedroomScene: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Wall */}
      <rect x="0" y="0" width="800" height="350" fill="#1e1b4b" rx="12" />
      {/* Window with moon */}
      <rect x="500" y="60" width="120" height="150" rx="4" fill="#0f172a" stroke="#475569" strokeWidth="3" />
      <line x1="560" y1="60" x2="560" y2="210" stroke="#475569" strokeWidth="2" />
      <line x1="500" y1="135" x2="620" y2="135" stroke="#475569" strokeWidth="2" />
      <circle cx="540" cy="100" r="15" fill="#fef3c7" />
      <circle cx="530" cy="95" r="13" fill="#0f172a" />
      {/* Stars through window */}
      {[515, 535, 570, 590].map((sx, i) => (
        <circle key={i} cx={sx} cy={80 + i * 15} r="1.5" fill="#fef3c7" opacity="0.6">
          <animate attributeName="opacity" values="0.3;0.8;0.3" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Floor */}
      <rect x="0" y="350" width="800" height="150" fill="#1e293b" />
      {/* Bed */}
      <rect x="80" y="250" width="350" height="100" rx="6" fill="#6366f1" />
      <rect x="75" y="240" width="20" height="120" rx="5" fill="#4f46e5" />
      <rect x="420" y="240" width="20" height="120" rx="5" fill="#4f46e5" />
      {/* Pillow */}
      <rect x="100" y="260" width="80" height="30" rx="10" fill="#e0e7ff" />
      {/* Blanket */}
      <rect x="100" y="290" width="320" height="55" rx="4" fill="#818cf8" />
      {/* Sleeping person */}
      <circle cx="140" cy="275" r="18" fill="#fbbf24" />
      {/* Closed eyes (sleeping) */}
      <line x1="130" y1="273" x2="140" y2="273" stroke="#1e293b" strokeWidth="2" />
      <line x1="145" y1="273" x2="155" y2="273" stroke="#1e293b" strokeWidth="2" />
      {/* Zzz */}
      <text x="180" y="250" fill="#a5b4fc" fontSize="14" fontWeight="bold" opacity="0.7">
        z
        <animate attributeName="opacity" values="0;0.7;0" dur="2s" repeatCount="indefinite" />
        <animateTransform attributeName="transform" type="translate" values="0,0;-5,-8;0,0" dur="2s" repeatCount="indefinite" />
      </text>
      <text x="200" y="235" fill="#a5b4fc" fontSize="18" fontWeight="bold" opacity="0.5">
        z
        <animate attributeName="opacity" values="0;0.5;0" dur="2.5s" repeatCount="indefinite" />
        <animateTransform attributeName="transform" type="translate" values="0,0;-3,-10;0,0" dur="2.5s" repeatCount="indefinite" />
      </text>
      <text x="220" y="215" fill="#a5b4fc" fontSize="22" fontWeight="bold" opacity="0.3">
        Z
        <animate attributeName="opacity" values="0;0.3;0" dur="3s" repeatCount="indefinite" />
        <animateTransform attributeName="transform" type="translate" values="0,0;-2,-12;0,0" dur="3s" repeatCount="indefinite" />
      </text>
      {/* Nightstand */}
      <rect x="460" y="290" width="60" height="65" rx="4" fill="#334155" />
      {/* Lamp */}
      <rect x="480" y="275" width="5" height="20" fill="#6b7280" />
      <path d="M470,275 Q483,260 495,275" fill="#fbbf24" opacity={0.4 + Math.sin(phase * 0.5) * 0.2} />
      <text x="400" y="470" textAnchor="middle" fill="#a5b4fc" fontSize="14" fontWeight="bold">Good Night 🌙</text>
    </svg>
  );
};
